import { useState } from 'react';
import { BookingState } from '../../modules/booking-state/BookingState';
import { Brand, VehicleDetails, VehicleDetailsRequest } from "../../modules/entities/Entities";
import { resetApplicationContext } from '../../utils/ApplicationContextUtils';
import { LoginByVinPageContext } from './LoginByVinPageContext';
import { LoginByVinPageFormData } from './LoginByVinPageFormData';
import { PersonalIdentityType } from '../../modules/login/PersonalIdentityType';
import { selectedIdentity } from '../../modules/login/PersonalIdentity';
import { withPageContext } from '../../modules/contexts/WithPageContext';
import { useViewProvider } from '../../modules/contexts/view-provider-context/ViewProvider';
import { useBrandNavigate } from '../../modules/navigation/UseBrandNavigate';
import { PageRoutes } from '../../modules/navigation/PageRoutes';
import { useGetVehicleDetails } from '../../modules/booking-api/BookingApi';
import { useBookingContext } from '../../modules/booking-state/UseBookingContext';
import { LoginByVinPageViewProps } from './LoginByVinPageViewProps';
import { useTracking } from '../../tracking';
import { Steps } from "../../modules/wizard/Steps";
import { BrandState, useBrand } from "../../modules/contexts/brand-context/BrandProvider";
import { NoVwVehicleModalContent } from "../../modules/modal/NoVwVehicleModalContent/NoVwVehicleModalContent";
import { useModalManager } from "../../modules/contexts/modal-manager-context/ModalManagerProvider";
import { isServiceUnavailable } from '../../modules/async/api-client';

function createVehicleDetailsRequest(data: LoginByVinPageFormData): VehicleDetailsRequest {
  return {
    ...selectedIdentity(data.identity),
    vin: data.vin ?? '',
  };
}

function ContextAccessor({ nationalIdNumber, passportNumber, vehicleDetails }: BookingState): LoginByVinPageContext {
  return {
    nationalIdNumber,
    passportNumber,
    vin: vehicleDetails?.vin,
  };
}

export const LoginByVinPage = withPageContext(ContextAccessor, () => {
  const viewProvider = useViewProvider();
  const modalManager = useModalManager();
  const [, setBookingState] = useBookingContext();
  const { setBrand } = useBrand();
  const [formData, setFormData] = useState<LoginByVinPageFormData>();
  // The form always starts empty — see LoginPage: a stored booking belongs to whoever used
  // the device before.
  const [initialFormData] = useState<LoginByVinPageFormData>(() => ({
    identity: {
      identityType: PersonalIdentityType.CustomerId,
      customerId: '',
      passportNumber: '',
    },
    vin: '',
  }));
  const [vehicleDetailsRequest, setVehicleDetailsRequest] = useState<VehicleDetailsRequest | null>(null);
  const navigate = useBrandNavigate();
  const trackingService = useTracking();

  useGetVehicleDetails(vehicleDetailsRequest, {
    onSuccess: (vehicleDetails: VehicleDetails | null) => {
      if (!vehicleDetails) {
        navigate(PageRoutes.LoginByVinError);
        return;
      }

      if (vehicleDetails.brand === Brand.Audi) {
        modalManager.open({
          title: 'Vehicle not found',
          content: NoVwVehicleModalContent,
          actions: [
            {
              title: 'OK',
              type: 'primary',
              key: 'ok',
              onClick(close) {
                close();
              },
            },
          ],
        });
        return;
      }

      const state: BookingState = {
        ...resetApplicationContext(),
        nationalIdNumber: selectedIdentity(formData!.identity).customerId,
        passportNumber: selectedIdentity(formData!.identity).passportNumber,
        registrationNumber: vehicleDetails.registrationNumber,
        vehicleDetails,
      };

      setBookingState(state);
      // The vehicle is found, so the customer goes straight on. A lookup of existing bookings
      // used to sit in between: its result has not been used since the bookings page was
      // switched off, yet its failure kept the customer on the login page. The brand comes
      // from the response — the booking state in this closure is still the previous one —
      // and is never Audi here: Audi vehicles were turned away above.
      setBrand(BrandState.Vw);
      navigate(PageRoutes.VehicleDetails, vehicleDetails.brand);
    },
    onError(error) {
      // Backend unreachable or timing out — let the global modal explain; don't pretend it was a login error.
      if (isServiceUnavailable(error)) {
        return;
      }
      navigate(PageRoutes.LoginByVinError);
    },
    skipGlobalError: true,
  });


  const onSubmit = (data: LoginByVinPageFormData): void => {
    const identityLabel = data.identity.identityType === PersonalIdentityType.CustomerId
      ? 'National ID number'
      : 'Passport number';
    trackingService.trackStepButtonClick({
      contentId: 'Next',
      step: Steps.Identification,
      identityLabel: identityLabel,
    });
    setFormData(data);
    setVehicleDetailsRequest(createVehicleDetailsRequest(data));
  };

  const viewProps: LoginByVinPageViewProps = {
    initialFormData,
    onSubmit,
  };

  return viewProvider.renderLoginByVinPage(viewProps);
});
